import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import {
  CreateSupplierTripInput,
  Currency,
  Measurement,
  RelocationInclusionType,
} from "@/gql/graphql";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { GenericFieldArray } from "@/lib/Components/Form/FieldArray/GenericFieldArray";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useTranslations } from "use-intl";
import { RelocationInclusionTypeInput } from "@/app/Relocations/Components/RelocationInclusionTypeInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { SupplierOfficeInput } from "@/app/Offices/Components/SupplierOfficeInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeRecordQuery } from "@/app/Offices/GraphQL/supplierOfficeRecordQuery";
import { DistanceAllowedInput } from "@/lib/Components/Form/Inputs/DistanceAllowedInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { Suspense } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { TripCard } from "@/app/Cities/Trips/Components/TripCard";

export function SupplierTripForm({ supplier }: { supplier: SupplierRecord }) {
  const { values, touched, initialValues, setFieldValue } =
    useFormState<CreateSupplierTripInput>();

  const t = useTranslations("relocation");

  const hireUnitLabel = t(`hire_unit_type.${supplier.hire_unit_type}`);
  const hireUnitsAllowedLabel = t(
    `hire_units_allowed_label.${supplier.hire_unit_type}`,
  );

  return (
    <>
      {!initialValues.officeA?.connect && !initialValues.officeB?.connect ? (
        <>
          <SupplierOfficeInput
            name="officeA.connect"
            label="Office A"
            getQueryVariables={(search) => ({
              first: 20,
              page: 1,
              search,
              archived: false,
              supplier_id: supplier.id,
            })}
          />
          <SupplierOfficeInput
            name="officeB.connect"
            label="Office B"
            getQueryVariables={(search) => ({
              first: 20,
              page: 1,
              search,
              archived: false,
              supplier_id: supplier.id,
            })}
          />
        </>
      ) : null}

      {values.officeA.connect && values.officeB.connect && supplier ? (
        <>
          <div className="col-span-full mb-6 px-6">
            <Suspense fallback={<Skeleton className="h-[254px] w-full" />}>
              <TripVisualization
                officeAId={values.officeA.connect}
                officeBId={values.officeB.connect}
              />
            </Suspense>
          </div>
        </>
      ) : null}

      <NumberInput
        name="hire_units_allowed"
        label={hireUnitsAllowedLabel}
        inputProps={{
          min: 0,
        }}
        onChange={(value) => {
          if (value === null) return;

          if (!touched.minimum_hire_units) {
            setFieldValue("minimum_hire_units", Math.floor(value / 2) + 1);
          }
        }}
      />

      <NumberInput
        name="extra_hire_units_allowed"
        label={`Extra ${hireUnitLabel.toLowerCase()}s allowed`}
      />

      <NumberInput
        name="minimum_hire_units"
        label={`Minimum ${hireUnitLabel.toLowerCase()}s allowed`}
      />
      <FormDivider />
      <DistanceAllowedInput
        name="distance_allowed"
        label={`${
          supplier?.measurement === Measurement.Metric ? "Kms" : "Mls"
        } allowed`}
        measurement={supplier?.measurement ?? Measurement.Metric}
      />

      <SwitchInput
        name="is_ferry_required"
        label="Is ferry required"
        tooltip="Do you want the customer to specify which ferry date they would prefer"
      />

      <GenericFieldArray
        label="Standard inclusions"
        fieldArrayKey="inclusions.upsert"
        newLineItemValues={{
          type: null,
          description: null,
          value: null,
        }}
      >
        {(item, index) => {
          return (
            <>
              <RelocationInclusionTypeInput
                name={`inclusions.upsert.${index}.type`}
                label="Type"
                excludeTypes={[RelocationInclusionType.Fuel]}
              />
              <CurrencyInput
                currency={supplier?.currency ?? Currency.Usd}
                name={`inclusions.upsert.${index}.value`}
                label="Monetary value"
                optionalLabel
              />
              <TextAreaInput
                name={`inclusions.upsert.${index}.description`}
                label="Description"
                className="col-span-full"
              />
            </>
          );
        }}
      </GenericFieldArray>
    </>
  );
}

function TripVisualization({
  officeAId,
  officeBId,
}: {
  officeAId: string;
  officeBId: string;
}) {
  const { data: officeAData } = useSuspenseGqlQuery(supplierOfficeRecordQuery, {
    id: officeAId,
  });

  const { data: officeBData } = useSuspenseGqlQuery(supplierOfficeRecordQuery, {
    id: officeBId,
  });

  return (
    <TripCard
      cityAId={officeAData.supplierOffice.city.id}
      cityBId={officeBData.supplierOffice.city.id}
    />
  );
}
